import type { HeightmapOptions } from "./heightmap.ts";
import type { HorizonHeightmapOptions } from "./horizon-heightmap.ts";

/**
 * The world's dimensions and the exact arguments every heightmap in it is
 * generated from.
 *
 * This module exists because the same numbers are now needed in two
 * processes: the browser, which builds the meshes, and `npm run bake`,
 * which generates the height data ahead of time and writes it to
 * public/heightmaps. If those two ever disagree by one octave, the baked
 * file is a different mountain from the one the trail was routed over and
 * the climb walks through mid-air - and nothing about the render would say
 * why. Neither side has its own copy of these; both import this file.
 */

export const HEIGHTMAP_SEED = 1334;
export const WORLD_SIZE = 620;
export const MAX_HEIGHT = 170;

/**
 * The far range's grid, in world units. Five times the massif's own
 * footprint, so its rim lands where exp2 fog has taken most of it - close
 * enough to read as mountains, far enough to read as distance.
 */
export const HORIZON_SIZE = WORLD_SIZE * 5.2;
/**
 * Deliberately *under* MAX_HEIGHT: ours is the highest mountain in this
 * world, and nothing on the skyline is allowed to top it.
 *
 * It is lowered here rather than by raising the massif, because MAX_HEIGHT
 * is the unit the whole climb is measured in - the trail's control points,
 * every flag's planted height, both ends of the camera crane and the
 * snowline are all fractions of it. Moving it moves all of them at once.
 * The far range answers to nothing, so the far range is what moves.
 *
 * 0.78 leaves real headroom rather than a tie. The generator's own peak
 * lands near 0.92 after its blur passes, so the tallest thing out there
 * reaches about 121 units against the massif's 168 - and it is four to
 * eight times further away besides, which is what turns a 30% difference
 * in height into the obvious difference in stature the page is about.
 */
export const HORIZON_MAX_HEIGHT = MAX_HEIGHT * 0.78;

/**
 * Mesh subdivisions per side, indexed by tier. The first entry is the
 * low-tier one (mobile, software renderers), the second desktop.
 *
 * Grid resolution is segments + 1, and that is the number the baked files
 * are keyed by - two tiers means two bakes of each map.
 */
export const MASSIF_SEGMENTS = { low: 512, high: 1024 } as const;
export const HORIZON_SEGMENTS = { low: 256, high: 512 } as const;

export function massifHeightmapOptions(resolution: number): HeightmapOptions {
  return {
    resolution,
    seed: HEIGHTMAP_SEED,
    worldWidth: WORLD_SIZE,
    massifWidth: WORLD_SIZE,
  };
}

export function horizonHeightmapOptions(resolution: number): HorizonHeightmapOptions {
  return {
    resolution,
    // Its own stream. Sharing the massif's seed would put the same ridge
    // pattern on the skyline as on the peak in front of it, at a different
    // scale - which the eye picks up immediately even when it cannot say
    // what is wrong.
    seed: HEIGHTMAP_SEED ^ 0x5f3a91,
    innerRadius: 0.4,
    outerRadius: 0.8,
  };
}

/** The four maps the site ships, and the filename each is baked under. */
export const BAKED_HEIGHTMAPS = [
  { key: "massif-513", kind: "massif", resolution: 513 },
  { key: "massif-1025", kind: "massif", resolution: 1025 },
  { key: "horizon-257", kind: "horizon", resolution: 257 },
  { key: "horizon-513", kind: "horizon", resolution: 513 },
] as const;

/**
 * Bumped by hand whenever anything above changes the shape of a map.
 *
 * The loader refuses a manifest that does not carry this number, so a
 * stale bake is a fallback to generating at runtime - slower, correct -
 * rather than a mountain that no longer matches the one the trail, the
 * camera clearances and the snowline were all tuned against.
 */
export const HEIGHTMAP_BAKE_VERSION = 1;
